<template>
	<div id="invoice-template">
		<div class="invoice--container">
			<header class="invoice--header" :class="invoice.status.name">
				<v-row class="invoice--heading">
					<v-col md="8" order="2" order-md="1">
						<h2 class="text-h2">Invoice</h2>
						<div class="invoice--number">
							# {{ invoice.id }}
						</div>
					</v-col>
					<v-col order="1" order-md="2">
						<div class="invoice--date d-flex justify-end align-center">
							<div class="mr-4 grey--text text--lighten-2">Status</div>
							<v-chip label dark :color="invoice.status.color">{{ invoice.status.name }}</v-chip>
						</div>
						<Logo />
					</v-col>
				</v-row>
			</header>

			<div class="invoice--to">
				<p>
					<strong>{{ invoice.project.client.billing_contact_name }}</strong><br />
					{{ invoice.project.client.name }}<br />
					{{ invoice.project.client.address1 }}<template v-if="invoice.project.client.address2">, {{ invoice.project.client.address2 }}</template><br />
					{{ invoice.project.client.city }}, {{ invoice.project.client.state.code }} {{ invoice.project.client.zipcode }}
				</p>
			</div>

			<div class="invoice--project">
				<v-row>
					<v-col cols="12" sm="6" md="3">
						<h6 class="text-h6">Job Name</h6>
						<p>{{ invoice.project.name }}</p>
					</v-col>
					<v-col cols="12" sm="6" md="3">
						<h6 class="text-h6">Job Number</h6>
						<p># {{ invoice.project.id }}</p>
					</v-col>
					<v-col cols="12" sm="6" md="3">
						<h6 class="text-h6">Job Type</h6>
						<p>{{ invoice.project.type }}</p>
					</v-col>
					<v-col cols="12" sm="6" md="3">
						<div v-if="! editingDate" class="d-flex">
							<div>
								<h6 class="text-h6">Invoice Date</h6>
								<p v-if="auth && invoice.status.name !== 'Paid'">
									<a class="table-link" @click="editingDate = true">{{ invoice.created_at | localDate }}</a>
									<a
										title="Set invoice date to today"
										class="teal--text ml-3 table-link text--sm"
										@click="setDateToToday">
										[ Set to Today ]
									</a>
								</p>
								<p v-else>{{ invoice.created_at | localDate }}</p>
							</div>
						</div>
						<div v-else id="invoice-date-picker" class="position-relative">
							<v-card width="290px" max-width="290px" elevation="3">
								<v-card-text class="pa-0 ma-0">
									<v-date-picker
										v-model="invoiceDate"
										class="pb-2"
										color="green lighten-1"></v-date-picker>
								</v-card-text>
								<v-divider />
								<v-card-actions class="align-items-center justify-center">
									<v-btn text small color="red" @click="editingDate = false">Cancel</v-btn>
									<v-btn text small color="success" @click="saveInvoice">Save Changes</v-btn>
								</v-card-actions>
							</v-card>
						</div>
					</v-col>
				</v-row>
			</div>

			<div class="invoice--items">
				<v-sheet outlined color="#DDDDDD" rounded elevation="0">
					<v-data-table
						:headers="headers"
						:items="items"
						:items-per-page="1000"
						:loading="loading"
						hide-default-footer>
						<template #item.quantity="{ item }">
							<v-edit-dialog v-if="invoice.editable" :return-value.sync="item.quantity" large @save="saveInvoice">
								{{ item.quantity }}
								<template #input>
									<v-text-field v-model="item.quantity"
										label="Quantity"
										placeholder="0"
										min="0"
										type="number"
										step="0.01"
										class="mt-4"
										hint="Enter 0 to remove this line item"
										:rules="[rules.numerical]"
										clearable
										outlined>
										<template #append>
											<v-icon
												color="red"
												@click="item.quantity++">
												mdi-plus
											</v-icon>
										</template>
										<template #prepend-inner>
											<v-icon
												color="green"
												@click="item.quantity--">
												mdi-minus
											</v-icon>
										</template>
									</v-text-field>
								</template>
							</v-edit-dialog>
							<template v-else>{{ item.quantity }}</template>
						</template>


						<template #item.job_type="{ item }">
							<v-edit-dialog v-if="invoice.editable" :return-value.sync="item.job_type" large @save="saveInvoice">
								<span v-if="item.job_type">{{ item.job_type }}</span>
								<span v-else class="grey--text lighten-2">Unknown</span>
								<template #input>
									<v-text-field v-model="item.job_type"
										label="Job Type"
										class="mt-4"
										hide-details
										outlined>
									</v-text-field>
								</template>
							</v-edit-dialog>
							<template v-else>
								<span v-if="item.job_type">{{ item.job_type }}</span>
								<span v-else class="grey--text lighten-2">Unknown</span>
							</template>
						</template>


						<template #item.properties="{ item }">
							<v-edit-dialog v-if="invoice.editable" :return-value.sync="item.job_type" large @save="saveInvoice">
								<div v-if="item.properties" class="text-pre">{{ item.properties }}</div>
								<span v-else class="grey--text lighten-2">--</span>
								<template #input>
									<v-textarea v-model="item.properties"
										label="Properties"
										class="mt-4"
										hide-details
										outlined>
									</v-textarea>
								</template>
							</v-edit-dialog>
							<template v-else>
								<div v-if="item.properties" class="text-pre" v-html="item.properties"></div>
								<span v-else class="grey--text lighten-2">--</span>
							</template>
						</template>


						<template #item.unit_price="{ item }">
							<v-edit-dialog v-if="invoice.editable" :return-value.sync="item.unit_price" large @save="saveInvoice">
								<span v-if="item.unit_price" class="font-mono">{{ item.unit_price | money }}</span>
								<span v-else class="grey--text lighten-2">$0.00</span>
								<template #input>
									<v-text-field v-model.number="item.unit_price"
										label="Unit Price"
										class="mt-4"
										type="number"
										step=".01"
										hide-details
										outlined>
										<template #prepend-inner>
											<v-icon>mdi-currency-usd</v-icon>
										</template>
									</v-text-field>
								</template>
							</v-edit-dialog>
							<template v-else>
								<span v-if="item.unit_price" class="font-mono">{{ item.unit_price | money }}</span>
								<span v-else class="grey--text lighten-2">$0.00</span>
							</template>
						</template>

						<template #item.line_total="{ item }">
							<span v-if="item.line_total" class="font-mono">{{ item.line_total | money }}</span>
							<span v-else class="grey--text lighten-2">$0.00</span>
						</template>
					</v-data-table>
				</v-sheet>

				<div id="invoice--totals-wrapper">
					<div v-if="auth" class="mr-md-auto">
						<v-btn elevation="0"
							small
							color="secondary lighten-1"
							:dark="invoice.editable"
							:disabled="!invoice.editable"
							@click="addItem">
							Add New Line Item
						</v-btn>
					</div>
					<div class="invoice--totals d-flex flex-column align-content-end justify-end">
						<div class="d-flex align-content-end justify-end pa-3" style="border-bottom: 1px solid #ddd">
							<div class="text-right text-uppercase mr-3 total-label"><strong>Sub Total: </strong></div>
							<div class="text-right font-mono total-column">{{ invoice.total | money }}</div>
						</div>
						<div class="d-flex align-content-end justify-end pa-3" style="border-bottom: 1px solid #ddd; background-color: #fff">
							<div class="text-right text-uppercase mr-3 total-label"><strong>Payments Received: </strong></div>
							<div class="text-right font-mono total-column">({{ invoice.received | money }})</div>
						</div>
						<div class="d-flex align-content-end justify-end pa-3">
							<div class="text-right text-uppercase mr-3 total-label"><strong>Total Due: </strong></div>
							<div class="text-right font-mono total-column">{{ invoice.total_due | money }}</div>
						</div>
					</div>
				</div>

				<div v-if="invoice.status.value === 'paid'" id="paid-stamp">
					<div class="paid-stamp-container">
						<div class="paid-stamp-wrapper">
							<div class="paid-stamp-label">Paid</div>
							<div class="paid-stamp-thanks">Thank you!</div>
						</div>
					</div>
				</div>
			</div>

			<footer v-if="length(invoice.payments) && (!auth || 1 !== auth.user_type)" class="invoice--footer">
				<v-row>
					<v-col>
						<p class="text-uppercase font-weight-bold">Transactions/Payments</p>
						<v-divider />
					</v-col>
				</v-row>
				<v-row class="mt-0">
					<v-col>
						<v-card outlined elevation="0">
							<v-data-table
								dense
								:headers="paymentsHeaders"
								:items="invoice.payments"
								hide-default-footer
								class="table-stripped">
								<template #item.type="{ item }">
									{{ item.type | capitalize }}
								</template>

								<template #item.amount="{ item }">
									<div class="font-mono">{{ item.amount | money }}</div>
								</template>

								<template #item.transaction_id="{ item }">
									<span class="font-mono">{{ item.transaction_id || '--' }}</span>
								</template>

								<template #item.paid_on="{ item }">
									{{ item.paid_on | localDate }}
								</template>
							</v-data-table>
						</v-card>
					</v-col>
				</v-row>
			</footer>

			<footer class="invoice--footer">
				<v-row>
					<v-col>
						<h6 class="text-h6 grey--text text--darken-1">Company Information</h6>
						<v-divider />
					</v-col>
				</v-row>
				<v-row>
					<v-col cols="12" sm="6" md="3" class="d-flex align-center pr-10">
						<Logo variant="filled" />
					</v-col>
					<v-col cols="12" sm="6" md="3">
						<h6 class="text-h6">Payable To</h6>
						<p class="mb-2">
							<span v-if="settings.billing_name">{{ settings.billing_name }}</span><br />
							<span v-if="settings.billing_tax_id">TAX ID: {{ settings.billing_tax_id }}</span><br />
						</p>
						<p>
							<span v-if="settings.billing_address1">{{ settings.billing_address1 }}</span><br />
							<span v-if="settings.billing_city && settings.billing_state_id && settings.billing_zipcode">
								{{ settings.billing_city }},
								{{ getState(settings.billing_state_id) }} {{ settings.billing_zipcode }}
							</span>
						</p>
					</v-col>
					<v-col cols="12" sm="6" md="3">
						<h6 class="text-h6">Phone</h6>
						<p>
							<span v-if="settings.billing_phone">{{ settings.billing_phone }}</span><br />
							<span v-if="settings.billing_phone_2">{{ settings.billing_phone_2 }}</span><br />
						</p>
					</v-col>
					<v-col cols="12" sm="6" md="3">
						<h6 class="text-h6">Web</h6>
						<p>
							www.belloldow.com<br />
							<span v-if="settings.billing_email">{{ settings.billing_email }}</span>
						</p>
					</v-col>
				</v-row>
			</footer>

			<template v-if="settings.billing_wiring_label">
				<v-divider />

				<footer class="invoice--footer py-5">
					<v-row>
						<v-col class="text-center grey--text text--darken-3">
							<p class="text-uppercase"><strong>Bell Oldow Wiring Instructions</strong></p>
							<ul class="d-inline-flex">
								<li class="mr-5 pr-4">{{ settings.billing_wiring_label }}</li>
								<li v-if="settings.billing_account_number" class="mr-5 pr-4">Account #: {{ settings.billing_account_number }}</li>
								<li v-if="settings.billing_wiring_number" class="mr-5 pr-4">Wiring #: {{ settings.billing_wiring_number }}</li>
								<li v-if="settings.billing_ach_number" class="mr-5">ACH#: {{ settings.billing_ach_number }}</li>
							</ul>
						</v-col>
					</v-row>
				</footer>
			</template>

			<v-divider />

			<div class="text-center">
				<p class="text-uppercase"><strong>Thank you for your business!</strong></p>

				<div v-if="invoice.slug" class="mt-6">
					<div class="text-uppercase grey--text text--darken-2 text--sm">Public access URL</div>
					<pre class="grey--text text--darken-2">{{ route('public.invoices.show', invoice) }}</pre>
				</div>
			</div>
		</div>
	</div>
</template>
<script>
import states from '@/Mixins/states'

export default {
	mixins: [ states ],
	props: {
		invoice: {
			type: Object,
			default: () => {},
			required: true
		}
	},
	data () {
		return {
			auth: this.$page.props.auth,
			settings: this.$page.props.settings,
			editingDate: false,
			invoiceDate: null,
			loading: false,
			headers: [
				{ text: 'QTY', value: 'quantity', align: 'center', sortable: false },
				{ text: 'Job Type', value: 'job_type', sortable: false },
				{ text: 'Properties', value: 'properties', sortable: false },
				{ text: 'Unit Price', value: 'unit_price', sortable: false, align: 'end' },
				{ text: 'Line Total', value: 'line_total', sortable: false, align: 'end' }
			],
			paymentsHeaders: [
				{
					text: 'Transaction ID',
					sortable: false,
					align: 'center',
					value: 'transaction_id'
				},
				{
					text: 'Type',
					sortable: false,
					align: 'center',
					value: 'type'
				},
				{
					text: 'Amount',
					sortable: false,
					align: 'center',
					value: 'amount'
				},
				{
					text: 'Paid On',
					sortable: false,
					align: 'start',
					value: 'paid_on'
				}
			],
			items: [],
			rules: {
				required: value => !!value || 'Required.',
				numerical: value => value >= 0 || 'Must be a valid number'
			}
		}
	},
	mounted () {
		this.items = this.invoice.data
		this.prepareDatePicker()
	},
	methods: {
		saveInvoice () {
			const data = { items: this.items, created_at: this.invoiceDate }
			this.$inertia.put(this.route('invoices.update', this.invoice), data, {
				only: ['invoice', 'flash'],
				onStart: () => {
					this.loading = true
					this.editingDate = false
				},
				onSuccess: () => {
					this.items = this.invoice.data
					this.$nextTick(() => this.prepareDatePicker())
				},
				onFinish: () => {
					this.loading = false
				},
				preserveScroll: true
			})
		},

		setDateToToday () {
			const date = new Date()
			const year = date.getFullYear()
			const month = String(date.getMonth() + 1).padStart(2, '0')
			const day = String(date.getDate()).padStart(2, '0')

			this.invoiceDate = `${year}-${month}-${day}`

			this.saveInvoice()
		},

		addItem () {
			this.items.push({
				quantity: 1,
				job_type: null,
				properties: null,
				unit_price: 0,
				line_total: 0
			})
		},

		prepareDatePicker () {
			// Extract the created_at date value
			const date = new Date(this.invoice.created_at)
			const year = date.getUTCFullYear()
			const month = String(date.getUTCMonth() + 1).padStart(2, '0')
			const day = String(date.getUTCDate()).padStart(2, '0')

			this.invoiceDate = `${year}-${month}-${day}`
		}
	}
}
</script>
